import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

export type AuthUser = {
  id: number;
  username: string;
  nickname: string;
  isAdmin: boolean;
};

type AuthContextValue = {
  user: AuthUser | null;
  ready: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginWithMusic: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const SSO_TRIED = "themix_sso_tried";
const SSO_SKIP = "themix_sso_skip";

function stripSsoParams(): boolean {
  const params = new URLSearchParams(window.location.search);
  const silentGuest = params.get("sso_silent") === "0";
  const failed = params.get("sso_failed") === "1";
  if (!silentGuest && !failed) return false;
  params.delete("sso_silent");
  params.delete("sso_failed");
  const q = params.toString();
  window.history.replaceState(null, "", `${window.location.pathname}${q ? `?${q}` : ""}${window.location.hash}`);
  return true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const me = await api<{ user: AuthUser | null }>("/api/auth/me");
    setUser(me.user);
  }, []);

  useEffect(() => {
    const cameBackGuest = stripSsoParams();

    void (async () => {
      try {
        const me = await api<{ user: AuthUser | null }>("/api/auth/me");
        if (me.user) {
          sessionStorage.removeItem(SSO_SKIP);
          setUser(me.user);
          setReady(true);
          return;
        }
        if (cameBackGuest || sessionStorage.getItem(SSO_SKIP) || sessionStorage.getItem(SSO_TRIED)) {
          setUser(null);
          setReady(true);
          return;
        }
        sessionStorage.setItem(SSO_TRIED, "1");
        const next = `${loc.pathname}${loc.search}` || "/";
        window.location.replace(`/api/auth/silent-start?next=${encodeURIComponent(next.startsWith("/") ? next : "/")}`);
      } catch {
        setUser(null);
        setReady(true);
      }
    })();
    // Premier chargement uniquement : le SSO silencieux ne doit pas relancer à chaque navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await api<{ user: AuthUser }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    sessionStorage.removeItem(SSO_SKIP);
    setUser(res.user);
  }, []);

  const register = useCallback(async (username: string, email: string, password: string) => {
    const res = await api<{ user: AuthUser }>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ username, email, password }),
    });
    sessionStorage.removeItem(SSO_SKIP);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    await api("/api/auth/logout", { method: "POST" });
    sessionStorage.setItem(SSO_SKIP, "1");
    sessionStorage.setItem(SSO_TRIED, "1");
    setUser(null);
  }, []);

  const loginWithMusic = useCallback(async () => {
    const next = `${window.location.pathname}${window.location.search}` || "/";
    const { url } = await api<{ url: string }>(`/api/auth/login-url?next=${encodeURIComponent(next)}`);
    window.location.href = url;
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, register, logout, loginWithMusic, refresh }),
    [user, ready, login, register, logout, loginWithMusic, refresh],
  );

  if (!ready) {
    return (
      <div className="min-h-dvh grid place-items-center px-6">
        <p className="font-display text-4xl text-gold">PGSet</p>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth hors AuthProvider");
  return ctx;
}
